/**
 * SEO.jsx  –  HaloSorath Cab Services
 *
 * Drop-in <SEO /> component powered by react-helmet-async.
 *
 * Install (if not already present):
 *   npm install react-helmet-async
 *
 * Wrap your <App /> (or index.jsx) with <HelmetProvider>:
 *   import { HelmetProvider } from "react-helmet-async";
 *   <HelmetProvider><App /></HelmetProvider>
 *
 * Usage examples:
 *   <SEO />                          → Home page defaults
 *   <SEO page="findDrivers" trip={tripState} />
 *   <SEO page="destination" destination={{ name: "Dwarka" }} />
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Site-wide constants ───────────────────────────────────────────────────────
const SITE = {
  name:        "HaloSorath Cab Services",
  url:         "https://halosorath.in",           // ← update to your real domain
  phone:       "+919265407669",
  whatsapp:    "https://wa.me/919265407669",
  logo:        "https://halosorath.in/og-logo.png", // 512×512 PNG
  ogImage:     "https://halosorath.in/og-cover.jpg", // 1200×630 JPG
  twitterHandle: "@halosorath",                         // update if you have one
  locality:    "Junagadh",
  region:      "Gujarat",
  country:     "IN",
  postalCode:  "362001",
  lat:         21.5222,
  lng:         70.4579,
  color:       "#f5c518",
};

// ─── Page-specific config ──────────────────────────────────────────────────────
const PAGE_DEFAULTS = {
  home: {
    title:       "HaloSorath Cab Services | Reliable Taxi in Junagadh & Gujarat",
    description: "Book verified cabs in Junagadh, Gujarat. One-way & round-trip outstation taxis to Somnath, Dwarka, Sasan Gir, Diu & more. 24×7 support, lowest fares, zero cancellation.",
    canonical:   "/",
    keywords:    "cab service Junagadh, taxi Junagadh, outstation cab Gujarat, Somnath taxi, Dwarka cab, Diu taxi, Sasan Gir cab, cab booking Gujarat, HaloSorath",
  },
  findDrivers: {
    title:       "Available Cabs | HaloSorath Cab Services",
    description: "Browse available, verified cabs for your route. Compare prices, ETAs and vehicle types — Sedan, SUV, Innova & Hatchback. Book instantly with no hidden charges.",
    canonical:   "/find-drivers",
    keywords:    "book cab, available taxi, outstation cab Gujarat, cab price, HaloSorath drivers",
  },
  destination: {
    title:       "Destinations | HaloSorath Cab Services",
    description: "Explore popular Gujarat destinations — Somnath, Dwarka, Diu, Sasan Gir, Porbandar & Bhavnath — with comfortable, affordable cab services from HaloSorath.",
    canonical:   "/destination",
    keywords:    "Gujarat tourist destinations cab, taxi to Somnath, Dwarka cab, Diu taxi, Gir cab",
  },
  services: {
  title: "Our Cab Services | HaloSorath – Airport, Outstation, Corporate",
  description: "Explore HaloSorath's full range of cab services — airport transfers, outstation rides, luxury rentals, corporate transport, and Tempo Traveller hire across Gujarat.",
  canonical: "/services",
  keywords: "cab services Gujarat, airport transfer Junagadh, outstation cab, luxury rental Gujarat, corporate cab",
},
howItWorks: {
  title: "How to Book a Cab | HaloSorath – 4 Simple Steps",
  description: "Book a cab in 4 easy steps on HaloSorath. No app, no advance payment, free cancellation. Enter pickup, browse cabs, confirm, and ride.",
  canonical: "/how-it-works",
  keywords: "how to book cab, cab booking process, HaloSorath booking steps",
},
contact: {
  title: "Contact HaloSorath | Call, WhatsApp or Email Us",
  description: "Reach HaloSorath 24×7 — call +91 92654 07669, WhatsApp, or email. Office at Junagadh, Gujarat. Fast replies guaranteed.",
  canonical: "/contact",
  keywords: "contact HaloSorath, cab booking Junagadh phone, taxi customer support Gujarat",
},
terms: {
  title: "Terms & Conditions | HaloSorath Cab Services",
  description: "Read HaloSorath's Terms & Conditions for cab bookings — cancellation policy, minimum km, advance payment, driver rules and more.",
  canonical: "/terms",
  keywords: "HaloSorath terms conditions, cab booking rules Gujarat",
},
privacy: {
  title: "Privacy Policy | HaloSorath Cab Services",
  description: "HaloSorath's privacy policy — how we collect, use, and protect your personal data when you book a cab with us.",
  canonical: "/privacy",
  keywords: "HaloSorath privacy policy, cab booking data protection",
},
cancellation: {
  title: "Cancellation & Refund Policy | HaloSorath",
  description: "HaloSorath's cancellation and refund policy — free cancellation before driver details are shared, fees after, and voucher eligibility.",
  canonical: "/cancellation",
  keywords: "HaloSorath cancellation policy, cab refund Gujarat",
},
};

// ─── JSON-LD schemas ───────────────────────────────────────────────────────────

/** LocalBusiness + TaxiService schema */
function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": `${SITE.url}/#organization`,
    name:           SITE.name,
    url:            SITE.url,
    logo:           SITE.logo,
    image:          SITE.ogImage,
    telephone:      SITE.phone,
    priceRange:     "₹₹",
    currenciesAccepted: "INR",
    paymentAccepted: "Cash, Credit Card, Debit Card, UPI",
    description:    PAGE_DEFAULTS.home.description,
    address: {
      "@type":           "PostalAddress",
      streetAddress:     "Junagadh",
      addressLocality:   SITE.locality,
      addressRegion:     SITE.region,
      postalCode:        SITE.postalCode,
      addressCountry:    SITE.country,
    },
    geo: {
      "@type":     "GeoCoordinates",
      latitude:    SITE.lat,
      longitude:   SITE.lng,
    },
    openingHoursSpecification: {
      "@type":     "OpeningHoursSpecification",
      dayOfWeek:   ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
      opens:       "00:00",
      closes:      "23:59",
    },
    sameAs: [
      SITE.whatsapp,
      // add Facebook / Instagram URLs here
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name:    "Cab Services",
      itemListElement: [
        { "@type":"Offer", itemOffered:{ "@type":"Service", name:"One-Way Outstation Cab" } },
        { "@type":"Offer", itemOffered:{ "@type":"Service", name:"Round-Trip Outstation Cab" } },
        { "@type":"Offer", itemOffered:{ "@type":"Service", name:"Airport Transfer"          } },
        { "@type":"Offer", itemOffered:{ "@type":"Service", name:"Corporate Rental"          } },
        { "@type":"Offer", itemOffered:{ "@type":"Service", name:"Tempo Traveller Hire"      } },
      ],
    },
    
  };
}

/** FAQ schema — mirrors the FAQS array in Home.jsx */
function faqSchema() {
  const faqs = [
    {
      q: "How can I book a cab?",
      a: "You can call us directly, WhatsApp us, or simply click 'Find Cabs Now' on our website. We're available 24×7 to assist you.",
    },
    {
      q: "How do I pay for the cab?",
      a: "We accept cash, credit/debit card, UPI, and mobile payments. Check with the driver before the trip to confirm accepted methods.",
    },
    {
      q: "Does the fare include tolls & parking?",
      a: "Yes! The fare includes all airport entry, toll, and parking charges. We strictly advise against paying anything extra to the driver.",
    },
    {
      q: "Can I change my booking after confirmation?",
      a: "Absolutely! Contact us directly to update your booking details. We'll work with you to make any necessary adjustments hassle-free.",
    },
    {
      q: "Is there a cancellation fee?",
      a: "No cancellation fees! Our zero-cancellation policy lets you change plans without any stress or extra charges. Book with peace of mind.",
    },
  ];

  return {
    "@context":  "https://schema.org",
    "@type":     "FAQPage",
    mainEntity:  faqs.map(({ q, a }) => ({
      "@type":        "Question",
      name:           q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/** BreadcrumbList schema */
function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: items.map(({ name, path }, idx) => ({
      "@type":   "ListItem",
      position:  idx + 1,
      name,
      item:      `${SITE.url}${path}`,
    })),
  };
}

/** WebSite schema with Sitelinks Searchbox */
function websiteSchema() {
  return {
    "@context":     "https://schema.org",
    "@type":        "WebSite",
    "@id":          `${SITE.url}/#website`,
    url:            SITE.url,
    name:           SITE.name,
    description:    PAGE_DEFAULTS.home.description,
    potentialAction: {
      "@type":      "SearchAction",
      target:       `${SITE.url}/find-drivers?pickup={search_term_string}`,
      "query-input":"required name=search_term_string",
    },
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * Props:
 *  page        – "home" | "findDrivers" | "destination"  (default: "home")
 *  trip        – { pickup, destination, date, returnDate } (for findDrivers page)
 *  destination – { name } (for destination page)
 *  noIndex     – boolean, adds noindex,nofollow
 */
export default function SEO({ page = "home", trip = {}, destination = {}, noIndex = false }) {
  const cfg = PAGE_DEFAULTS[page] || PAGE_DEFAULTS.home;

  // Dynamic overrides
  let title       = cfg.title;
  let description = cfg.description;
  let canonical   = `${SITE.url}${cfg.canonical}`;
  let keywords    = cfg.keywords;

  // FindDrivers: enrich with route info
  if (page === "findDrivers" && trip.pickup && trip.destination) {
    const from  = trip.pickup.split(",")[0].trim();
    const to    = trip.destination.split(",")[0].trim();
    const type  = trip.returnDate ? "Round-Trip" : "One-Way";
    title       = `${from} to ${to} Cab (${type}) | HaloSorath`;
    description = `Book a ${type.toLowerCase()} cab from ${from} to ${to}. Verified drivers, lowest fares, toll included. Instant confirmation — HaloSorath.`;
    keywords    = `${from} to ${to} cab, ${from} to ${to} taxi, outstation cab ${from}, ${type.toLowerCase()} cab Gujarat`;
  }

  // Destination page
  if (page === "destination" && destination.name) {
    title       = `Cab to ${destination.name} | HaloSorath Taxi Service Gujarat`;
    description = `Book affordable, verified cabs to ${destination.name} from anywhere in Gujarat. Comfortable rides, professional drivers, no hidden charges — HaloSorath.`;
    keywords    = `cab to ${destination.name}, taxi ${destination.name}, ${destination.name} trip, Gujarat cab ${destination.name}`;
    canonical   = `${SITE.url}/destination/${destination.name.toLowerCase()}`;
  }

  // Build JSON-LD array
  const schemas = [localBusinessSchema(), websiteSchema()];

  if (page === "home") {
    schemas.push(faqSchema());
    schemas.push(
      breadcrumbSchema([
        { name: "Home", path: "/" },
      ])
    );
  }

  if (page === "findDrivers") {
    schemas.push(
      breadcrumbSchema([
        { name: "Home",           path: "/"              },
        { name: "Find Drivers",   path: "/find-drivers"  },
      ])
    );
  }

  if (page === "destination" && destination.name) {
    schemas.push(
      breadcrumbSchema([
        { name: "Home",                path: "/"                                          },
        { name: "Destinations",        path: "/destination"                               },
        { name: destination.name,      path: `/destination/${destination.name.toLowerCase()}` },
      ])
    );
  }

  return (
    <Helmet>
      {/* ── Primary ── */}
      <html lang="en" />
      <title>{title}</title>
      <meta name="description"              content={description} />
      <meta name="keywords"                 content={keywords} />
      <meta name="author"                   content="HaloSorath Cab Services" />
      <meta name="robots"                   content={noIndex ? "noindex,nofollow" : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"} />
      <link rel="canonical"                 href={canonical} />

      {/* ── Open Graph ── */}
      <meta property="og:type"              content="website" />
      <meta property="og:site_name"         content={SITE.name} />
      <meta property="og:title"             content={title} />
      <meta property="og:description"       content={description} />
      <meta property="og:url"               content={canonical} />
      <meta property="og:image"             content={SITE.ogImage} />
      <meta property="og:image:width"       content="1200" />
      <meta property="og:image:height"      content="630" />
      <meta property="og:image:alt"         content={`${SITE.name} – Cab Booking`} />
      <meta property="og:locale"            content="en_IN" />

      {/* ── Twitter / X Card ── */}
      <meta name="twitter:card"             content="summary_large_image" />
      <meta name="twitter:site"             content={SITE.twitterHandle} />
      <meta name="twitter:title"            content={title} />
      <meta name="twitter:description"      content={description} />
      <meta name="twitter:image"            content={SITE.ogImage} />
      <meta name="twitter:image:alt"        content={`${SITE.name} – Cab Booking`} />

      {/* ── Mobile / PWA ── */}
      <meta name="viewport"                 content="width=device-width, initial-scale=1, viewport-fit=cover" />
      <meta name="theme-color"              content={SITE.color} />
      <meta name="apple-mobile-web-app-capable"            content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style"   content="black-translucent" />
      <meta name="apple-mobile-web-app-title"              content="HaloSorath" />

      {/* ── Geo ── */}
      <meta name="geo.region"               content={`${SITE.country}-GJ`} />
      <meta name="geo.placename"            content={SITE.locality} />
      <meta name="geo.position"             content={`${SITE.lat};${SITE.lng}`} />
      <meta name="ICBM"                     content={`${SITE.lat}, ${SITE.lng}`} />

      {/* ── WhatsApp link preview ── */}
      <meta property="og:phone_number"      content={SITE.phone} />

      {/* ── Extra Advanced SEO Meta ── */}

<meta name="language" content="English" />
<meta name="revisit-after" content="1 days" />
<meta name="distribution" content="global" />
<meta name="rating" content="general" />
<meta name="coverage" content="Worldwide" />
<meta name="target" content="all" />
<meta name="HandheldFriendly" content="True" />
<meta name="MobileOptimized" content="320" />
<meta name="apple-touch-fullscreen" content="yes" />

{/* Google */}
<meta itemProp="name" content={title} />
<meta itemProp="description" content={description} />
<meta itemProp="image" content={SITE.ogImage} />

{/* Additional Open Graph */}
<meta property="og:email" content="support@halosorath.in" />
<meta property="og:phone_number" content={SITE.phone} />
<meta property="og:country-name" content="India" />
<meta property="business:contact_data:street_address" content="Junagadh" />
<meta property="business:contact_data:locality" content="Junagadh" />
<meta property="business:contact_data:region" content="Gujarat" />
<meta property="business:contact_data:postal_code" content="362001" />
<meta property="business:contact_data:country_name" content="India" />

{/* Cab / Travel SEO */}
<meta name="classification" content="Cab Booking Service" />
<meta name="category" content="Taxi Service, Cab Booking, Gujarat Travel" />
<meta name="subject" content="Cab Booking in Gujarat" />
<meta name="summary" content={description} />
<meta name="url" content={canonical} />
<meta name="identifier-URL" content={SITE.url} />

{/* Local SEO */}
<meta name="geo.country" content="IN" />
<meta name="geo.region" content="IN-GJ" />
<meta name="geo.placename" content="Junagadh" />

{/* Search Engine Verification */}
<meta name="googlebot" content="index,follow,max-video-preview:-1,max-image-preview:large,max-snippet:-1" />
<meta name="bingbot" content="index,follow" />

{/* Theme */}
<meta name="msapplication-TileColor" content={SITE.color} />
<meta name="msapplication-navbutton-color" content={SITE.color} />
<meta name="apple-mobile-web-app-status-bar-style" content="default" />

{/* WhatsApp & Telegram Preview */}
<meta property="og:image:type" content="image/jpeg" />
<meta property="og:image:secure_url" content={SITE.ogImage} />

{/* Rich Snippet */}
<meta name="format-detection" content="telephone=yes" />

{/* Copyright */}
<meta name="copyright" content="HaloSorath Cab Services" />
<meta name="reply-to" content="support@halosorath.in" />
<meta name="owner" content="HaloSorath" />

{/* Cache Control */}
<meta httpEquiv="Cache-Control" content="public, max-age=31536000" />

      {/* ── JSON-LD structured data ── */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema, null, 0)}
        </script>
      ))}
    </Helmet>
  );
}